import { useRef } from 'react';
import { getResizedImage } from 'shared-helpers/common';
import { useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import { getPredefinedPictures } from 'lib/files';
import { ModalButton } from 'components/common';

import type { CoursePicturePosition } from 'shared-types';

interface Props {
  onSelect: (picture: string) => void;
  picturePosition?: CoursePicturePosition;
  selectedPicture?: string;
}

const PictureSelector = ({
  onSelect,
  picturePosition = 'center top',
  selectedPicture
}: Props) => {
  const modalButtonRef = useRef<any>(undefined);

  const { messages, theme } = useReduxState();

  const pictures = getPredefinedPictures();

  const handleOptionClick = (picture: string) => {
    onSelect(picture);
    modalButtonRef.current?.hideModal();
  };

  return (
    <div className="selector">
      <ModalButton
        category="lighter"
        getModalButton={(modalButton) => (modalButtonRef.current = modalButton)}
        modalProps={{
          closeButton: true,
          title: messages.settingsSelectPicture,
          width: 'wide'
        }}
        size="header"
        text={messages.settingsSelectPicture}
      >
        <div className="content">
          <div className="options">
            {pictures.map((picture) => (
              <button
                className={`option ${
                  picture === selectedPicture ? 'option-active' : ''
                }`}
                key={picture}
                onClick={() => handleOptionClick(picture)}
                type="button"
              >
                <div
                  className="picture"
                  style={{
                    backgroundImage: `url(${getResizedImage({
                      size: 480,
                      url: picture
                    })})`,
                    backgroundPosition: picturePosition
                  }}
                />
              </button>
            ))}
          </div>
        </div>
      </ModalButton>

      <style jsx>{`
        .options {
          display: flex;
          flex-wrap: wrap;
          gap: 20px;
        }
        .option {
          background-color: transparent;
          border: 0;
          border-radius: ${theme.cardBorderRadius};
          box-shadow: 0 0 0 6px transparent;
          cursor: pointer;
          height: 100px;
          overflow: hidden;
          padding: 0;
          position: relative;
          transition: box-shadow 0.3s;
          width: calc(50% - 10px);
        }
        .option:focus,
        .option:hover {
          box-shadow: 0 0 0 6px ${theme.lightGreyDark};
        }
        .option-active,
        .option-active:focus,
        .option-active:hover {
          box-shadow: 0 0 0 6px ${theme.textColor};
        }
        .picture {
          background-size: cover;
          border-radius: ${theme.cardBorderRadius};
          height: 100%;
          width: 100%;
        }
        @media (min-width: ${breakpoints.small}px) {
          .option {
            height: 110px;
            width: calc(33.333% - 13.333px);
          }
        }
        @media (min-width: ${breakpoints.medium}px) {
          .option {
            height: 120px;
            width: calc(25% - 15px);
          }
        }
      `}</style>
    </div>
  );
};

export default PictureSelector;
